import { Component, OnDestroy, OnInit, ViewChild, TemplateRef } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router, Params } from '@angular/router';
import { Subject } from 'rxjs/internal/Subject';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';

import { CardInterface, Currency, Lesson, Pupil } from '@df/interfaces';

import { PupilService } from '@app/student/services/pupil.service';
import { TaskService } from '@app/core/services/task.service';
import { CountingService } from '@app/core/services/counting.service';

import { MAIN_DOMAIN } from '@app/app.constants';
import { declensionOfNumerals } from '@app/_utils/helpers';

import { CardSettings } from '@df/models';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { MatDialog } from '@angular/material/dialog';
import { Location } from '@angular/common';
import { HandlerService } from '@app/_services/handler.service';

@Component({
    selector: 'app-pupil-task-choice',
    templateUrl: './pupil-task-choice.component.html',
    styleUrls: ['./pupil-task-choice.component.scss']
})
export class PupilTaskChoiceComponent implements OnInit, OnDestroy {
    @ViewChild('solvedModal', { static: true }) solvedModal: ModalDirective;
    @ViewChild('checkModal', { static: true }) checkModal: ModalDirective;
    @ViewChild('authorizateonErrorMsg', { static: true }) authorizateonErrorTemplate: TemplateRef<any>;
    studentId: number;
    lesson: Lesson;
    student: Pupil;
    cardNumber: string = '';
    buttons: number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9];
    errorMessage: string = 'Идет проверка доступных уроков. Подождите немного.';
    modalMessage: string = 'Эта вариант уже решен';
    main_domain = MAIN_DOMAIN;
    cardNotFound: boolean = false;
    card: CardInterface;
    currencies: Currency[] = [];
    riskCharacterSelected: boolean = false;
    isAbsent: boolean = false;
    showSpiner: boolean = false;

    dialogRef: any;

    private destroy$: Subject<boolean> = new Subject<boolean>();
    private cardResultId: number;

    public allCardSettings: Array<CardSettings> = [];
    public isAccessCard = false;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private pupilService: PupilService,
        private taskService: TaskService,
        private handlerService: HandlerService,
        private countingService: CountingService,
        private location: Location,
        public dialog: MatDialog
    ) {}

    ngOnInit(): void {
        this.init();
    }

    ngOnDestroy(): void {
        this.destroy$.next(true);
    }

    openDialog(): void {
        this.dialogRef = this.dialog.open(this.authorizateonErrorTemplate, {
            width: '50%',
            height: '240px'
        });
    }

    closeDialog(): void {
        this.dialogRef.close();
    }

    buttonClick(num: number) {
        const numStr = num.toString();
        this.cardNumber = this.cardNumber + numStr;
    }

    clearInput() {
        this.cardNumber = '';
    }

    handleError(error: HttpErrorResponse, message: string, redirect: boolean) {
        if (error.status === 404) {
            this.errorMessage = message;
            if (redirect === true) {
                setTimeout(() => {
                    this.pupilService.nullPupil();
                    this.router.navigate(['/class', this.student.student_class]);
                }, 5000);
            }
        } else {
            console.log(error);
            this.errorMessage = 'Произошла ошибка. Обратитесь к преподавателю.';
        }
    }

    searchCard() {
        if (this.checkIfStudentIsAbsent()) {
            this.modalMessage = 'Учитель отметил вас отсутствующим на уроке!';
            this.cardNotFound = false;
            this.showModal();
            return;
        }
        const card_number = +this.cardNumber;
        if (card_number && this.lesson && this.studentId) {
            this.taskService.getCardToNumber(card_number, this.lesson.id, this.studentId).subscribe(resp => {
                this.handlerService.checkErrors(resp);

                if (resp.error) {
                    this.cardNumber = '';
                }

                if (!resp.error) {
                    this.showSpiner = true;
                    this.card = resp.data;
                    this.getCardData(false);
                    setTimeout(() => {
                        if (this.isAccessCard || !this.card.prev_results) {
                            if (
                                (this.card.box && this.card.box.risk_character) ||
                                (this.card.box && this.card.box.check_character)
                            ) {
                                this.getCardData(true);
                                this.showCharacterModal();
                            } else {
                                this.router.navigate([
                                    'student',
                                    'pupil',
                                    this.student.id,
                                    this.lesson.id,
                                    this.card.id
                                ]);
                            }
                        }
                    }, 1500);
                }
            });
        }
    }

    checkIfStudentIsAbsent() {
        if (this.lesson) {
            if (this.lesson.attending_students.indexOf(+this.studentId) === -1) {
                this.isAbsent = true;
                return true;
            }
        }
        return false;
    }

    hideModal() {
        this.showSpiner = false;
        this.solvedModal.hide();
        this.clearInput();
    }

    showModal() {
        this.solvedModal.show();
    }

    hideCharacterModal() {
        this.checkModal.hide();
        this.clearInput();
    }

    showCharacterModal() {
        this.checkModal.show();
    }

    exitClicked() {
        this.hideModal();
        this.location.back();
    }

    selectCharacter(isRisk: boolean, character) {
        this.riskCharacterSelected = isRisk;
        this.showSpiner = false;
        this.hideCharacterModal();
        this.pupilService.setRiskCharacter(isRisk);
        this.pupilService.setSelectCharacter(character, isRisk);
        this.router.navigate(['/student', 'pupil', this.student.id, this.lesson.id, this.card.id]);
    }

    public declensionOfNumerals(amount: number): string {
        return declensionOfNumerals(amount, this.currencies[0]);
    }

    private init(): void {
        this.pupilService.removeRiskCharacter();
        this.pupilService.removeSelectCharacter();

        this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params: Params) => {
            this.studentId = params['pupil_id'];
            this.getLesson();
            this.pupilService
                .queryPupil(this.studentId)
                .pipe(takeUntil(this.destroy$))
                .subscribe(pupil => {
                    this.student = pupil;
                });
        });
    }

    private getLesson(): void {
        this.taskService
            .getCurrentPupil(this.studentId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                resp => {
                    this.lesson = resp;

                    if (this.currencies.length === 0) {
                        this.taskService.getCurrencyForLesson(this.lesson).subscribe(response => {
                            if (response.count !== 0) {
                                this.currencies = response.results;
                                this.taskService.setCurrency(this.currencies[0]);
                            }
                        });
                    }
                },
                error => {
                    if (error.status === 401) {
                        this.openDialog();
                    }
                }
            );
    }

    private getCardData(risk: boolean): void {
        if (this.card.prev_results) {
            this.cardResultId = Number(this.card.prev_results.card_result_id);
        } else {
            this.cardResultId = null;
        }
        const data = {
            student_id: this.studentId,
            card_id: this.card.id,
            risk: false,
            card_result_id: this.cardResultId
        };
        if (risk) {
            data.risk = true;
        }
        this.countingService
            .getCardSettings(data)
            .pipe(takeUntil(this.destroy$))
            .subscribe(resp => {
                this.allCardSettings.push(resp);
                this.isAccessCard = resp.attempts >= 1;
            });
    }
}
